import {useFetchBuilderReference} from '@/builder/hooks/useFetchBuilderReference'
import {useCategory} from '@salesforce/commerce-sdk-react'
import PropTypes from 'prop-types'
import React, {useState, useEffect} from 'react'
import Brand from './brand'
import NavItem from './navItem'
import {filterOutLMNCategories} from './LcLmnSuppressionUtility/learningCenterLMNSuppressionUtility'
// import {useTpaSuppressionData} from 'hec/context/tpa-config'
import {useClientSitePreferences} from 'hec/context/site-preferences'
import {getTpaContextFromRequest} from 'hec/helpers/ssrTpaHelper'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

const SHOP_BY_BRAND_CATEGORY_ID = 'shop-by-brand'

const MainNav = ({brandContent, attributes}) => {
    // const {shouldSuppressContent} = useTpaSuppressionData() // TODO: Need to fix TPA suppression logic
    const {LMN} = useClientSitePreferences()
    const siteId = getConfig().app.commerceAPI.parameters.siteId
    // Use consistent initial values for both server and client to avoid hydration mismatch.
    // The client updates after mount via useEffect.
    const serverHostname = `${siteId.toLowerCase()}.com`
    const [hostname, setHostname] = useState(serverHostname)
    const [reqContext, setReqContext] = useState(null)
    useEffect(() => {
        setHostname(window.location.hostname)
        setReqContext({url: window.location.href, headers: {cookie: document.cookie}})
    }, [])
    const {afid} = reqContext ? getTpaContextFromRequest(reqContext) : {afid: null}
    const isLMNAllowed =
        afid === null
            ? true
            : LMN?.lmnListAllowsTPA
                  ?.split(',')
                  ?.map((s) => s.trim())
                  .includes(afid)
    const hasBrandContentOverride = !!brandContent?.id
    const {data: brandContentData} = useFetchBuilderReference({
        model: 'symbol',
        shouldQuery: hasBrandContentOverride,
        referenceId: brandContent?.id
    })

    const [openNavItem, setOpenNavItem] = useState(null)
    const {data: categoriesTree} = useCategory({
        parameters: {id: 'root', levels: 2}
    })

    const navigationSections = new Map()

    const categoriesList = Array.isArray(categoriesTree?.categories)
        ? categoriesTree.categories
        : []

    const handleNavItemToggle = (id) => {
        setOpenNavItem((prev) => (prev === id ? null : id))
    }

    categoriesList.forEach((category, index) => {
        const isLMNCategory = category?.c_ownerClass === 'LMN'
        const isHiddenFromMenu = !category?.c_showInMenu && !category?.c_showAsMenuOption

        if (isLMNCategory && !isLMNAllowed) return
        if (isHiddenFromMenu) return

        let processedCategory = category

        if (!isLMNAllowed) {
            // Filter out LMN subcategories if not allowed
            processedCategory = filterOutLMNCategories(processedCategory)
        }

        if (processedCategory?.c_alternativeUrl) {
            // TODO: Issue with external URLs, this would overwrite them. If they ever want one we'd need to update.
            // Could have them add a boolean category attribute "isExternalUrl" instead of maintaining complex list of URLs
            try {
                const altUrl = new URL(processedCategory.c_alternativeUrl)
                if (altUrl.hostname !== hostname) {
                    const lastSegment = altUrl.pathname
                        .split('/')
                        .filter((item) => item !== '')
                        .pop()

                    processedCategory = {
                        ...processedCategory,
                        c_alternativeUrl: `https://${hostname}/${lastSegment}`
                    }
                }
            } catch {
                // c_alternativeUrl is a relative path, no rewrite needed
            }
        }

        let section = processedCategory?.c_navigationSection

        if (!section || typeof section !== 'string') {
            section = 'Shop'
        }

        if (!navigationSections.has(section)) {
            navigationSections.set(section, new Map())
        }

        // Keyed by forEach index to preserve SFCC's category sort order within each section.
        // Map insertion order is guaranteed, so [...map.values()] yields categories in SFCC order after filtering.
        navigationSections.get(section).set(index, processedCategory)
    })

    const sortedNavigationSections = [...navigationSections.entries()]
        .sort(([a], [b]) => {
            if (a === 'Shop') return 1
            if (b === 'Shop') return -1
            return a.localeCompare(b)
        })
        .map(([sectionTitle, categoriesMap]) => ({
            sectionTitle,
            categories: [...categoriesMap.values()]
        }))

    return (
        <div {...attributes}>
            {sortedNavigationSections.map(({sectionTitle, categories}, sectionIndex) => (
                <React.Fragment key={sectionTitle}>
                    <h4
                        className={`body-header-lg text-blue-05 mb-[0.625rem]${
                            sectionIndex > 0 ? ' mt-[1.625rem]' : ''
                        }`}
                    >
                        {sectionTitle}
                    </h4>
                    <div>
                        {categories.map((category) => (
                            <React.Fragment key={category.id}>
                                {hasBrandContentOverride &&
                                category?.id === SHOP_BY_BRAND_CATEGORY_ID ? (
                                    <Brand
                                        content={brandContentData}
                                        isOpen={openNavItem === category.id}
                                        onToggle={() => handleNavItemToggle(category.id)}
                                        data-testid="mainNav-Brand"
                                    />
                                ) : (
                                    <NavItem
                                        category={category}
                                        isOpen={openNavItem === category.id}
                                        onToggle={() => handleNavItemToggle(category.id)}
                                        data-testid="mainNav-NavItem"
                                    />
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </React.Fragment>
            ))}
        </div>
    )
}

MainNav.propTypes = {
    brandContent: PropTypes.object,
    attributes: PropTypes.any
}

export default MainNav

export const MainNavDefinition = {
    component: MainNav,
    name: 'MainNavigation',
    inputs: [
        {
            name: 'brandContent',
            type: 'reference',
            model: 'symbol',
            friendlyName: 'Brand content',
            required: false
        }
    ],
    noWrap: true
}
